"use client";

import { useEffect, useMemo, useState } from "react";
import { getCourseVideoFrameStyle, normalizeCourseVideoStyle } from "../lib/courseVideoStyle";
import videoFrame from "./courseVideoFrame.module.scss";
import CourseQuizModal from "./CourseQuizModal";
import { confirmSectionReading } from "../lib/courseSectionsApi";

function isBunnyUrl(url) {
    return (
        typeof url === "string" &&
        (url.includes("mediadelivery.net") || url.includes("bunnycdn.com") || url.includes(".b-cdn.net"))
    );
}

function addEmbedParams(url) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}autoplay=true&loop=false&muted=false&preload=true&responsive=true`;
}

function isYouTubeUrl(url) {
    return typeof url === "string" && (url.includes("youtube.com") || url.includes("youtu.be"));
}

function getYouTubeEmbedUrl(url) {
    if (!url || typeof url !== "string") return null;
    let videoId = "";
    if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1]?.split("&")[0] || "";
    } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
    } else if (url.includes("embed/")) {
        videoId = url.split("embed/")[1]?.split("?")[0] || "";
    }
    return videoId
        ? `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`
        : null;
}

const iframeFill = {
    border: 0,
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
};

/**
 * @param {{
 *   episodes: { key: string, url: string, title?: string }[],
 *   quizId: number | null,
 *   videoStyle?: unknown,
 *   courseId?: string | number,
 *   sectionKey?: string,
 *   sectionTitle?: string,
 *   contentLocked?: boolean,
 *   sectionQuizPassed?: boolean,
 *   reading?: string,
 *   readingConfirmed?: boolean,
 *   onProgressChange?: () => void,
 * }} props
 */
export default function CourseSectionVideos({
    episodes,
    quizId,
    videoStyle,
    courseId,
    sectionKey,
    sectionTitle,
    contentLocked = false,
    sectionQuizPassed = false,
    gated = false,
    sectionThreshold,
    reading = "",
    readingConfirmed = false,
    onProgressChange,
}) {
    const mode = normalizeCourseVideoStyle(videoStyle);
    const frameStyle = useMemo(() => getCourseVideoFrameStyle(mode), [mode]);
    const portraitClass = mode === "portrait" ? videoFrame.portraitAccordion : "";

    const [quizModalOpen, setQuizModalOpen] = useState(false);

    const [username, setUsername] = useState("");
    useEffect(() => {
        try {
            const stored = JSON.parse(localStorage.getItem("user") || "null");
            if (stored?.user_display_name) setUsername(stored.user_display_name);
        } catch {
            // ignore malformed/missing localStorage user
        }
    }, []);

    const [readingOpen, setReadingOpen] = useState(false);
    const [honorChecked, setHonorChecked] = useState(false);
    const [confirmingReading, setConfirmingReading] = useState(false);
    const [confirmReadingError, setConfirmReadingError] = useState("");

    const handleConfirmReading = async () => {
        if (!honorChecked || confirmingReading) return;
        setConfirmingReading(true);
        setConfirmReadingError("");
        try {
            const res = await confirmSectionReading(courseId, sectionKey);
            if (!res.ok) {
                setConfirmReadingError(res.message || "تعذر حفظ التأكيد، حاول مرة أخرى.");
                return;
            }
            onProgressChange?.();
        } finally {
            setConfirmingReading(false);
        }
    };

    const [signedBunnyUrls, setSignedBunnyUrls] = useState({});
    const [bunnySignErrors, setBunnySignErrors] = useState({});

    const bunnySourceUrls = useMemo(() => {
        if (contentLocked) return [];
        const urls = [];
        for (const ep of episodes || []) {
            const u = (ep?.url || "").trim();
            if (u && isBunnyUrl(u)) urls.push(u);
        }
        return Array.from(new Set(urls));
    }, [episodes, contentLocked]);

    useEffect(() => {
        let cancelled = false;

        const fetchSigned = async (url) => {
            if (!isBunnyUrl(url)) return;
            try {
                const qs = new URLSearchParams({ url }).toString();
                const res = await fetch(`/api/bunny/sign?${qs}`, { method: "GET" });
                if (!res.ok) {
                    const payload = await res.json().catch(() => ({}));
                    const message = payload?.message || `Signing failed (${res.status})`;
                    if (!cancelled) {
                        setBunnySignErrors((prev) => (prev[url] ? prev : { ...prev, [url]: message }));
                    }
                    return;
                }
                const data = await res.json();
                const signed = typeof data?.embed_url === "string" ? data.embed_url.trim() : "";
                if (!cancelled && signed) {
                    setSignedBunnyUrls((prev) => (prev[url] ? prev : { ...prev, [url]: signed }));
                }
            } catch (err) {
                if (!cancelled) {
                    setBunnySignErrors((prev) =>
                        prev[url] ? prev : { ...prev, [url]: err?.message || "Signing request failed" }
                    );
                }
            }
        };

        bunnySourceUrls.forEach((u) => {
            void fetchSigned(u);
        });

        return () => {
            cancelled = true;
        };
    }, [bunnySourceUrls]);

    if (!episodes?.length && !quizId) {
        return null;
    }

    const episodeLabel = (ep) => {
        if (ep.title && ep.title.trim()) return ep.title.trim();
        const m = /^episod_(\d+)$/.exec(ep.key);
        return m ? `الدرس ${m[1]}` : ep.key;
    };

    const LOCK_MSG = "( يجب تخطى الاختبارات السابقة أولاً )";
    const READING_LOCK_MSG = "( أكمل قراءة المقال أولاً )";
    const readingPending = Boolean(reading) && Boolean(quizId) && !readingConfirmed;

    const lockedLessonRows = contentLocked
        ? (episodes || [])
              .filter((ep) => (ep?.url || "").trim())
              .map((ep) => (
                  <div key={ep.key} className="mb-3">
                      <div
                          className="d-inline-flex align-items-center gap-2 small text-muted"
                          dir="rtl"
                      >
                          <i className="bi bi-lock-fill flex-shrink-0" aria-hidden />
                          <span className="fw-medium">{episodeLabel(ep)}</span>
                      </div>
                  </div>
              ))
        : [];

    const episodeBlocks = (contentLocked ? [] : episodes || []).map((ep) => {
                const rawUrl = (ep.url || "").trim();
                if (!rawUrl) return null;

                const yt = isYouTubeUrl(rawUrl) ? getYouTubeEmbedUrl(rawUrl) : null;
                if (yt) {
                    return (
                        <div key={ep.key} className="mb-4">
                            <div className="small text-muted mb-2">{episodeLabel(ep)}</div>
                            <div className={`rounded-3 overflow-hidden border ${portraitClass}`.trim()} style={frameStyle}>
                                <iframe
                                    title={episodeLabel(ep)}
                                    src={yt}
                                    loading="lazy"
                                    style={iframeFill}
                                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        </div>
                    );
                }

                const signed = signedBunnyUrls[rawUrl];
                const signErr = bunnySignErrors[rawUrl];
                const iframeSrc = signed ? addEmbedParams(signed) : null;

                if (isBunnyUrl(rawUrl) && !iframeSrc) {
                    return (
                        <div key={ep.key} className="mb-4">
                            <div className="small text-muted mb-2">{episodeLabel(ep)}</div>
                            <div
                                className={`rounded-3 bg-dark text-white p-2 text-center border ${portraitClass}`.trim()}
                                style={{
                                    ...frameStyle,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <small className="px-2">
                                    {signErr
                                        ? `تعذر الوصول إلى رابط فيديو : ${signErr}`
                                        : "جاري تجهيز رابط الفيديو ..."}
                                </small>
                            </div>
                        </div>
                    );
                }

                const genericEmbed =
                    iframeSrc || (rawUrl.includes("/embed/") || rawUrl.includes("mediadelivery") ? addEmbedParams(rawUrl) : null);

                if (!genericEmbed) {
                    return (
                        <div key={ep.key} className="mb-3">
                            <div className="small text-muted mb-2">{episodeLabel(ep)}</div>
                            <p className="text-muted small mb-0">صيغة الفيديو غير مدعومة</p>
                        </div>
                    );
                }

                return (
                    <div key={ep.key} className="mb-4">
                        <div className="small text-muted mb-2">{episodeLabel(ep)}</div>
                        <div className={`rounded-3 overflow-hidden border ${portraitClass}`.trim()} style={frameStyle}>
                            <iframe
                                src={genericEmbed}
                                title={episodeLabel(ep)}
                                loading="lazy"
                                style={iframeFill}
                                allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
                                allowFullScreen
                            />
                        </div>
                    </div>
                );
            });

    const hasEpisodes = episodeBlocks.some(Boolean);
    const hasLockedLessonHints = lockedLessonRows.length > 0;
    const hasLessonArea = hasEpisodes || hasLockedLessonHints;

    return (
        <div className="course-section-videos">
            {hasLockedLessonHints ? <div className="rounded-3">{lockedLessonRows}</div> : null}
            {hasEpisodes ? (
                <div className="rounded-3">
                    {episodeBlocks}
                </div>
            ) : null}

            {reading && contentLocked ? (
                <div className={`${hasLessonArea ? "mt-3 pt-3 border-top" : ""}`} dir="rtl">
                    <div className="d-inline-flex align-items-center gap-2 small text-muted mb-3">
                        <i className="bi bi-lock-fill flex-shrink-0" aria-hidden />
                        <span className="fw-medium">معلومات إضافية + المراجع</span>
                    </div>
                </div>
            ) : null}

            {reading && !contentLocked ? (
                <div className={`${hasLessonArea ? "mt-3 pt-3 border-top" : ""}`} dir="rtl">
                    <button
                        type="button"
                        className="btn btn-light w-100 d-flex align-items-center justify-content-between rounded-3 border mb-3"
                        aria-expanded={readingOpen}
                        onClick={() => setReadingOpen((v) => !v)}
                    >
                        <span className="fw-bold">معلومات إضافية + المراجع</span>
                        <i className={`bi ${readingOpen ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden />
                    </button>
                    {readingOpen ? (
                        <>
                            <div className="rounded-3 border bg-light p-3 mb-3">
                                <div dangerouslySetInnerHTML={{ __html: reading }} />
                            </div>
                            {readingPending ? (
                                <div className="rounded-3 border p-3 mb-3">
                                    <div className="form-check mb-3">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id={`reading-honor-${sectionKey}`}
                                            checked={honorChecked}
                                            onChange={(e) => setHonorChecked(e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor={`reading-honor-${sectionKey}`}>
                                            {"أقر أنا "}
                                            <span className="fw-bold">{username || "الطالب"}</span>
                                            {" أنني أتممت - بعون الله وكرمه - قراءة المقال كاملاً وجاهز للاختبار"}
                                        </label>
                                    </div>
                                    {confirmReadingError ? (
                                        <div className="text-danger small mb-2">{confirmReadingError}</div>
                                    ) : null}
                                    <button
                                        type="button"
                                        className="btn btn-primary rounded-pill"
                                        disabled={!honorChecked || confirmingReading}
                                        onClick={handleConfirmReading}
                                    >
                                        {confirmingReading ? "جاري الحفظ..." : "أكملت القراءة"}
                                    </button>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
            ) : null}

            {quizId ? (
                <div className={`${hasLessonArea || reading ? "mt-3 pt-3 border-top" : ""}`}>
                    {sectionQuizPassed ? (
                        <span className="badge bg-light text-success border border-success rounded-pill px-3 py-2">
                            <i className="bi bi-check-circle-fill me-1" aria-hidden />
                            اجتزت هذا الاختبار
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="btn btn-outline-primary rounded-pill"
                            disabled={contentLocked || readingPending}
                            title={contentLocked ? LOCK_MSG : readingPending ? READING_LOCK_MSG : undefined}
                            onClick={() => !contentLocked && !readingPending && setQuizModalOpen(true)}
                        >
                            <i className="bi bi-pencil-square me-2" aria-hidden />
                            فتح الاختبار
                        </button>
                    )}
                    <CourseQuizModal
                        open={quizModalOpen}
                        quizId={quizId}
                        sectionTitle={sectionTitle || "الاختبار"}
                        courseId={courseId}
                        sectionKey={sectionKey}
                        gated={gated}
                        sectionThreshold={sectionThreshold}
                        onClose={() => setQuizModalOpen(false)}
                        onQuizPassed={() => {
                            onProgressChange?.();
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
}
